import {Color} from '@benchling/nucleus-ui';
import {StyleSheet, css} from 'aphrodite';
import React from 'react';

import {FONT_FAMILY_PROXIMA, FONT_PROXIMA_11, FONT_PROXIMA_12} from 'styles/FontConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';

interface MenuPanelRowProps {
  label: string;
  description: string | null;
  isSelected?: boolean;
  isDisabled?: boolean;
  onClick?: () => void;
  /**
   * Optional badge component to display next to the label.
   * Use badge components like <BetaBadge /> or <PreviewBadge />.
   */
  badge?: JSX.Element;
  dataTestAttrs?: Pick<DataTestAttrs, 'key'>;
}

export default function MenuPanelRow({
  label,
  description,
  isSelected,
  isDisabled,
  onClick,
  badge,
  dataTestAttrs,
}: MenuPanelRowProps): React.ReactElement {
  return (
    <div
      className={css(styles.row, isSelected && styles.rowSelected, isDisabled && styles.rowDisabled)}
      onClick={isDisabled ? undefined : onClick}
      {...getTestProps({component: 'MenuPanelRow', element: 'root', ...(dataTestAttrs ?? {})})}
    >
      <div className={css(styles.labelRow)}>
        <div className={css(styles.option)}>{label}</div>
        {badge && <div className={css(styles.badge)}>{badge}</div>}
      </div>
      {description && <div className={css(styles.description)}>{description}</div>}
    </div>
  );
}

const styles = StyleSheet.create({
  row: {
    fontFamily: FONT_FAMILY_PROXIMA,
    padding: '7px 8px',
    cursor: 'pointer',
    ':hover': {
      backgroundColor: Color.GRAY_50,
    },
  },
  rowSelected: {
    backgroundColor: Color.GRAY_50,
  },
  rowDisabled: {
    cursor: 'default',
    opacity: 0.5,
    ':hover': {
      backgroundColor: 'transparent',
    },
  },
  labelRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  option: {
    color: Color.BLACK,
    ...FONT_PROXIMA_12,
  },
  badge: {
    display: 'flex',
    alignItems: 'center',
    height: '16px',
    marginLeft: '8px',
    overflow: 'hidden',
    borderRadius: '4px',
  },
  description: {
    color: Color.GRAY_500,
    ...FONT_PROXIMA_11,
  },
});
